/**
 * Copy for the bank dossier — the Legado (B3 · Gestão de Garantias) field set
 * that every work order now carries so the bank sees the same information it
 * always had, wherever it sees the OS.
 *
 * Labels reproduce the legacy vocabulary word for word where the field is the
 * same (`constants/legacy.ts`): that is the contract the bank teams know.
 * Section titles follow the v2 guide (sentence case).
 */

/**
 * The dossier comes from the avaliahub-api when `REACT_APP_AVALIAHUB_API_URL`
 * is set, and then never from the mock. Without that URL (development without
 * the avaliahub-api) this flag renders a deterministic mock so the screens can
 * still be reviewed.
 */
export const BANK_DOSSIER_MOCK_ENABLED = true;

export type DossierSectionId =
  | 'financing'
  | 'timeline'
  | 'parties'
  | 'contacts'
  | 'charges'
  | 'files'
  | 'related';

export const dossierSectionOrder: DossierSectionId[] = [
  'financing',
  'timeline',
  'parties',
  'contacts',
  'charges',
  'files',
  'related',
];

export const Constants = {
  emptyValue: '—',
  yes: 'Sim',
  no: 'Não',
  notInformed: 'não informado',
  previewNotice:
    'Dados do dossiê do banco em pré-visualização: ainda não são salvos nem enviados.',
  readOnlyNotice:
    'Somente administradores e operadores do banco editam os dados bancários.',
  loadFailedNotice: (reason: string): string =>
    `Dados bancários indisponíveis no momento (${reason}). Eles não serão alterados ao salvar.`,

  api: {
    notConfigured: 'URL do avaliahub-api não configurada.',
    unavailable: 'Serviço de dados bancários indisponível.',
    unauthenticated:
      'Serviço de dados bancários indisponível: a sessão não foi reconhecida.',
    invalid: 'Resposta inválida do serviço de dados bancários.',
  },

  save: {
    dossierFailed: (reason: string): string =>
      `Dados da OS salvos, mas os dados bancários não: ${reason}`,
    workOrderFailed: (reason: string): string =>
      `Dados bancários salvos, mas os dados da OS não: ${reason}`,
    bothFailed: (reason: string, dossierReason: string): string =>
      `${reason} Os dados bancários também não foram salvos: ${dossierReason}`,
    createdWithoutDossier: (referenceNumber: number, reason: string): string =>
      `OS ${referenceNumber} cadastrada, mas os dados bancários não foram ` +
      `salvos (${reason}). Complete-os em "editar".`,
  },
  demoUnavailable:
    'Indisponível no modo de demonstração: o serviço do banco ainda não está conectado.',

  strip: {
    title: 'Dossiê do banco',
    control: 'Nº Controle Interno / OS',
    folder: 'Nº da Pasta',
    request: 'Solicitação',
    ordinal: (ordinal: number): string => `${ordinal}ª solicitação`,
    subStatus: 'Sub-Status',
    channel: 'Canal',
    appraisalValue: 'Valor Avaliação',
    appraisalPending: 'aguardando laudo',
    latestReport: 'Laudo mais Recente',
    noLatestReport: 'Sem laudo anexado',
    related: (count: number): string =>
      count === 1
        ? '1 solicitação relacionada'
        : `${count} solicitações relacionadas`,
    noRelated: 'Única solicitação da pasta',
  },

  sections: {
    financing: 'Financiamento e operação de crédito',
    timeline: 'Prazos e linha do tempo',
    parties: 'Empresa de avaliação e banco solicitante',
    contacts: 'Contatos da solicitação',
    charges: 'Cobranças associadas',
    files: 'Arquivos associados',
    related: 'Solicitações relacionadas',
  } as Record<DossierSectionId, string>,

  financing: {
    infoTitle: 'Informações sobre o financiamento',
    valuesTitle: 'Valores',
    reportTitle: 'Tipo de laudo',
    fields: {
      control: 'Nº Controle Interno / Ordem de Serviço',
      originalProposal: 'Nº Proposta Original do Banco',
      previousProposal: 'Nº Anterior da Proposta Original do Banco',
      owner: 'Proprietário do bem',
      proponent: 'Proponente / Comprador / Garantidor / Mutuário',
      proponentDocument: 'Nº CPF / CNPJ do Proponente',
      operationType: 'Tipo de Operação',
      requestKind: 'Tipo da Solicitação',
      purpose: 'Propósito da Avaliação / Finalidade do Laudo/Linha',
      fgts: 'Uso FGTS',
      eligibility: 'Elegibilidade do Proponente',
      financingValue: 'Valor Financiamento',
      purchaseValue: 'Valor Compra Venda',
      ltv: 'LTV',
      propertyValue: 'Valor Imóvel',
      appraisalValue: 'Valor Avaliação',
      originalReport: 'Laudo Original',
    },
  },

  timeline: {
    timelineTitle: 'Linha do tempo',
    datesTitle: 'Datas',
    empty: 'Nenhuma data registrada ainda.',
    fields: {
      report_delivery_date: 'Data para Entrega do Laudo',
      bank_due_date: 'Data Venc. - Banco',
      created_at: 'Data Criação',
      appraisal_company_due_date: 'Data Venc. - Empresa de Avaliação',
      assignment_date: 'Data Atribuição',
      engineer_assignment_date: 'Data Atribuição Engenheiro',
      acceptance_date: 'Data Aceite',
      inspection_date: 'Data Vistoria',
      probable_inspection_date: 'Provável Data Vistoria',
      report_completion_date: 'Data de Finalização do Laudo',
      report_received_at: 'Laudo Recebido',
      cancellation_date: 'Data Cancelamento',
      cancellation_request_date: 'Data Solicitação Cancelamento',
      revision_request_date: 'Solicitação de Revisão',
      revision_closed_date: 'Solicit. Revisão Finalizada/Rejeitada',
      rectification_request_date: 'Solicitação de Retificação',
      rectification_closed_date: 'Solicit. Retificação Finalizada/Rejeitada',
    },
  },

  parties: {
    companyTitle: 'Empresa de Avaliação',
    company: {
      name: 'Empresa de Avaliação',
      engineer: 'Engenheiro',
      escalated: 'Engenheiro/Representante Legal Escalado',
      revisionType: 'Tipo de Revisão',
      revisionResponsible: 'Responsável pela Revisão',
      additionalInfo: 'Informações Adicionais',
    },
    bankTitle: 'Banco Solicitante',
    bank: {
      bank: 'Banco',
      channel: 'Canal',
      requester: 'Solicitante',
    },
    engineeringTitle: 'Depto de Engenharia do Banco',
    engineering: {
      department: 'Depto de Engenharia do Banco',
      analyst: 'Analista da Área',
      manager: 'Nome do Gerente',
      analysis: 'Análise do Depto de Engenharia do Banco',
    },
  },

  contacts: {
    empty: 'Nenhum contato registrado.',
    columns: {
      type: 'Tipo Contato',
      fullName: 'Nome Completo',
      organization: 'Organização',
      code: 'Código de Contato',
      email: 'e-mail',
      businessPhone: 'Telefone Comercial',
      extension: 'Ramal',
      homePhone: 'Telefone Residencial',
      mobilePhone: 'Telefone Celular',
      fax: 'FAX',
    },
  },

  charges: {
    empty: 'Nenhuma cobrança associada.',
    columns: {
      feeType: 'Tipo de Honorário',
      chargeType: 'Tipo de Cobrança',
      bankCharged: 'Valor Cobrado do Banco',
      appraiserFee: 'Honorário do Avaliador',
      platformFee: 'Honorários banco/plataforma',
      createdAt: 'Data de Criação',
      notes: 'Observações',
    },
    totalBank: 'Total de Cobranças - Banco',
    totalFees: 'Total dos Honorários',
  },

  files: {
    empty: 'Nenhum arquivo associado.',
    showAllFolder: 'Exibir todos os arquivos associados à pasta',
    selectAll: 'Selecionar todos',
    latest: 'Laudo mais Recente',
    download: 'Baixar',
    downloadSelected: (count: number): string =>
      count === 1 ? 'Baixar 1 arquivo' : `Baixar ${count} arquivos`,
    columns: {
      type: 'Tipo do Arquivo',
      control: 'Nº Controle Interno / OS',
      os: 'OS',
      createdAt: 'Data de Criação',
      size: 'Tamanho',
    },
  },

  related: {
    description: 'Outras solicitações da mesma pasta',
    current: 'Esta solicitação',
    empty: 'Esta é a única solicitação da pasta.',
    open: 'Abrir OS',
    notMigrated: 'OS fora da plataforma',
    ordinal: (ordinal: number): string => `${ordinal}ª Solicitação`,
    columns: {
      os: 'OS',
      control: 'Nº Controle Interno / OS',
      ordinal: 'Solicitação',
      company: 'Empresa de Avaliação',
      reportType: 'Tipo de Laudo',
      status: 'Status',
      createdAt: 'Data Criação',
    },
  },

  address: {
    streetType: 'tipo de logradouro:',
    floor: 'andar:',
    condominium: 'condomínio / empreendimento:',
    original: 'endereço original (como veio do banco):',
    newArea: 'área nova (CEP inexistente):',
  },

  registry: {
    totalUnits: 'nº total de unidades vinculadas/autônomas:',
    unitsTitle: 'Unidades vinculadas',
    unitsEmpty: 'Nenhuma unidade vinculada.',
    originalReport: 'laudo original:',
    columns: {
      number: 'N° da Matrícula',
      quantity: 'Quantidade',
      unitType: 'Tipo Unidade',
      description: 'Descrição',
    },
  },

  characteristics: {
    blocks: 'nº blocos/torres:',
    newConstruction: 'construção nova:',
    rural: 'imóvel rural:',
  },

  list: {
    control: 'controle interno:',
    folder: 'pasta:',
    appraisalValue: 'valor avaliação:',
    columnControl: 'Controle / Pasta',
    columnAppraisal: 'Valor avaliação',
    kpiTotal: 'Ordens de serviço',
    kpiAppraised: 'Valor avaliado (total)',
    kpiWithValue: 'Com valor de avaliação',
    createdFrom: 'Criada de',
    createdTo: 'até',
    pageHint: 'nesta página',
  },

  form: {
    bankSection: 'Dados do banco e do financiamento',
    engineeringSubsection: 'Depto de Engenharia do Banco',
    contactsSection: 'Contatos da solicitação',
    addContact: 'adicionar contato',
    removeContact: 'remover contato',
    addUnit: 'adicionar unidade',
    removeUnit: 'remover unidade',
    ltvHint: 'calculado: financiamento ÷ valor do imóvel',
    originalReportHint:
      'informe o laudo anterior quando a solicitação for revisão ou retificação',
  },
};

/**
 * Vocabularies used by the mock and by the creation form. They will come from
 * the API as facets/enums; the front must not depend on these literals.
 */
export const channelOptions = [
  'Agência',
  'Correspondente',
  'Digital',
  'Pós-venda',
];

export const operationTypeOptions = [
  'Aquisição',
  'Refinanciamento',
  'Portabilidade',
  'Construção',
  'Home equity',
];

export const requestKindOptions = ['Isolado', 'Lote', 'Revisão', 'Retificação'];

export const revisionRequestKinds = new Set(['Revisão', 'Retificação']);

export const eligibilityOptions = [
  'Elegível',
  'Elegível com restrições',
  'Em análise',
];

export const creditLinePurposeOptions = [
  'SFH – Aquisição de imóvel residencial',
  'SFI – Financiamento imobiliário',
  'Crédito com garantia de imóvel',
  'Portabilidade de financiamento',
];

export const streetTypeOptions = [
  { value: 'R', label: 'R — Rua' },
  { value: 'Av', label: 'Av — Avenida' },
  { value: 'Al', label: 'Al — Alameda' },
  { value: 'Tv', label: 'Tv — Travessa' },
  { value: 'Est', label: 'Est — Estrada' },
  { value: 'Rod', label: 'Rod — Rodovia' },
  { value: 'Pç', label: 'Pç — Praça' },
];

export const contactTypeOptions = [
  'Proponente',
  'Proprietário',
  'Síndico',
  'Corretor',
  'Analista do banco',
  'Responsável pela vistoria',
  'Gerente',
];

export const feeTypeOptions = [
  'Honorário do Avaliador',
  'Valor Cobrado do Banco',
  'Vistoria extra',
  'Retificação',
];

export const unitTypeOptions = [
  'Apartamento',
  'Vaga de garagem',
  'Depósito',
  'Loja',
  'Sala',
];
